import { RunAsyncUntilCompleteTimedOutError } from './RunAsyncUntilCompleteTimedOutError';

export type RunAsyncUntilCompleteFnResponse<T> =
  | { type: 'not-completed' }
  | { type: 'completed'; data: T };

type Args<T> = {
  fn: () => Promise<RunAsyncUntilCompleteFnResponse<T>>;
  intervalMs: number;
  timeoutMs?: number;
  timeoutMsPassed?: number;
};

export async function runAsyncUntilComplete<T>({
  fn,
  intervalMs,
  timeoutMs = 240_000,
  timeoutMsPassed = 0,
}: Args<T>): Promise<T> {
  const remainingTimeout = timeoutMs - timeoutMsPassed;
  if (remainingTimeout <= 0)
    throw new RunAsyncUntilCompleteTimedOutError(timeoutMs);

  const delayedFn = () =>
    new Promise<RunAsyncUntilCompleteFnResponse<T>>((resolve, reject) => {
      setTimeout(async () => {
        fn().then(resolve).catch(reject);
      }, intervalMs);
    });

  const initialTime = new Date().getTime();

  const response = await delayedFn();

  const newTime = new Date().getTime();
  const deltaTime = newTime - initialTime;

  if (response.type === 'completed') return response.data;

  return runAsyncUntilComplete({
    fn,
    intervalMs,
    timeoutMs,
    timeoutMsPassed: timeoutMsPassed + deltaTime,
  });
}
